// ExcelInventoryManager - import/export Excel inventario fisico (wizard multi-step + audit log)
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import { Download, History, FileDown, FileUp } from 'lucide-react';
import { InventoryUploadStep } from './excel-inventory/InventoryUploadStep';
import { InventoryPreviewStep } from './excel-inventory/InventoryPreviewStep';
import { InventoryResultStep } from './excel-inventory/InventoryResultStep';
import { InventoryAuditLogDialog } from './excel-inventory/InventoryAuditLogDialog';

const STEPS = {
  UPLOAD: 0,
  VALIDATE: 1,
  PREVIEW: 2,
  CONFIRM: 3,
  RESULT: 4
};

export default function ExcelInventoryManager({ onImportComplete }) {
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [loading, setLoading] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [validation, setValidation] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setLoading(true);
    try {
      const response = await api.get('/inventory/excel/template', { responseType: 'blob' });
      downloadBlob(response.data, `modello_inventario_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Template scaricato');
    } catch (error) {
      toast.error('Errore nel download del template');
    } finally {
      setLoading(false);
    }
  };

  const handleExportInventory = async () => {
    setLoading(true);
    try {
      const response = await api.get('/inventory/excel/export', { responseType: 'blob' });
      downloadBlob(response.data, `inventario_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Inventario esportato');
    } catch (error) {
      toast.error('Errore nell\'export dell\'inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/inventory/excel/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSessionId(response.data.session_id);
      setValidation(response.data.validation);
      setCurrentStep(STEPS.PREVIEW);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella validazione');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      const response = await api.post('/inventory/excel/import', {
        session_id: sessionId,
        confirm: true
      });

      setImportResult(response.data);
      setCurrentStep(STEPS.RESULT);

      if (response.data.status === 'success') {
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message);
      }

      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'import');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = async () => {
    if (sessionId) {
      try {
        await api.post('/inventory/excel/import', {
          session_id: sessionId,
          confirm: false
        });
      } catch (e) {
        // ignora
      }
    }
    resetWizard();
  };

  const resetWizard = () => {
    setCurrentStep(STEPS.UPLOAD);
    setSelectedFile(null);
    setSessionId(null);
    setValidation(null);
    setImportResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Azioni header */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={handleDownloadTemplate} disabled={loading}>
          <FileDown className="h-4 w-4 mr-2" />
          Scarica Template
        </Button>
        <Button variant="outline" onClick={handleExportInventory} disabled={loading}>
          <Download className="h-4 w-4 mr-2" />
          Esporta Inventario
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowAuditLog(true)}
        >
          <History className="h-4 w-4 mr-2" />
          Log Operazioni
        </Button>
      </div>

      {/* Wizard import */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5" />
            Import Inventario da Excel
          </CardTitle>
          <CardDescription>
            Carica un file Excel per importare articoli in inventario
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Indicatore avanzamento */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className={currentStep >= STEPS.UPLOAD ? 'text-primary font-medium' : 'text-muted-foreground'}>
                1. Carica File
              </span>
              <span className={currentStep >= STEPS.PREVIEW ? 'text-primary font-medium' : 'text-muted-foreground'}>
                2. Anteprima
              </span>
              <span className={currentStep >= STEPS.RESULT ? 'text-primary font-medium' : 'text-muted-foreground'}>
                3. Risultato
              </span>
            </div>
            <Progress value={(currentStep / 4) * 100} className="h-2" />
          </div>

          {currentStep === STEPS.UPLOAD && (
            <InventoryUploadStep
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              onValidate={handleValidate}
              loading={loading}
            />
          )}

          {currentStep === STEPS.PREVIEW && (
            <InventoryPreviewStep
              validation={validation}
              loading={loading}
              onCancel={handleCancelImport}
              onConfirm={handleConfirmImport}
            />
          )}

          {currentStep === STEPS.RESULT && (
            <InventoryResultStep
              importResult={importResult}
              onReset={resetWizard}
            />
          )}
        </CardContent>
      </Card>

      <InventoryAuditLogDialog
        open={showAuditLog}
        onOpenChange={setShowAuditLog}
      />
    </div>
  );
}
